/**
 * Bestiary Store
 *
 * Zustand store for the creature template library (machina_bestiary).
 * DB-backed with Supabase realtime subscriptions.
 */

import { create } from 'zustand';
import type { BestiaryEntry } from '@machina/core';
import { trpc } from '../lib/trpc';
import { createSubscription } from '../lib/realtimeHelpers';

// =============================================================================
// DB TYPE (for realtime payload casting)
// =============================================================================

interface DbBestiaryEntry {
  id: string;
  campaign_id: string;
  slug: string | null;
  name: string;
  cr: string | null;
  size: string;
  creature_type: string | null;
  alignment: string | null;
  ac: number;
  max_hp: number;
  abilities: Record<string, number>;
  saves: Record<string, number>;
  actions: unknown[];
  tactics: Record<string, unknown>;
  speed: number;
  resistances: string[];
  immunities: string[];
  vulnerabilities: string[];
  condition_immunities: string[];
  img_url: string | null;
  iso_sprites: { south?: string; north?: string; east?: string; west?: string } | null;
  notes: string | null;
  is_named: boolean;
  tags: string[];
  source: string;
  created_at: string;
  updated_at: string;
}

function dbToBestiaryEntry(db: DbBestiaryEntry): BestiaryEntry {
  return {
    id: db.id,
    campaignId: db.campaign_id,
    slug: db.slug,
    name: db.name,
    cr: db.cr,
    size: db.size,
    creatureType: db.creature_type,
    alignment: db.alignment,
    ac: db.ac,
    maxHp: db.max_hp,
    abilities: {
      str: db.abilities?.str ?? 10,
      dex: db.abilities?.dex ?? 10,
      con: db.abilities?.con ?? 10,
      int: db.abilities?.int ?? 10,
      wis: db.abilities?.wis ?? 10,
      cha: db.abilities?.cha ?? 10,
    },
    saves: db.saves ?? {},
    actions: (db.actions ?? []) as BestiaryEntry['actions'],
    tactics: (db.tactics ?? {}) as BestiaryEntry['tactics'],
    speed: db.speed ?? 30,
    resistances: db.resistances ?? [],
    immunities: db.immunities ?? [],
    vulnerabilities: db.vulnerabilities ?? [],
    conditionImmunities: db.condition_immunities ?? [],
    imgUrl: db.img_url,
    isoSprites: db.iso_sprites ?? undefined,
    notes: db.notes,
    isNamed: db.is_named,
    tags: db.tags ?? [],
    source: db.source,
    createdAt: db.created_at,
    updatedAt: db.updated_at,
  };
}

/**
 * Seed iso sprite cache for every live mob instance whose base name matches
 * the given bestiary template. Called when a template's isoSprites update
 * via realtime so other players see the sprites without a reload.
 */
function seedIsoSpritesForBestiaryInstances(entry: BestiaryEntry): void {
  const saved = entry.isoSprites;
  if (!saved) return;
  void Promise.all([
    import('./isoSpriteStore'),
    import('./mobStore'),
  ]).then(([{ useIsoSpriteStore }, { useMobStore }]) => {
    const isoStore = useIsoSpriteStore.getState();
    const mobs = useMobStore.getState().mobs;
    for (const mob of mobs.values()) {
      const mobAny = mob as unknown as { bestiaryId?: string | null };
      if (mobAny.bestiaryId !== entry.id) continue;
      for (const f of ['south', 'north', 'east', 'west'] as const) {
        const url = saved[f];
        if (url) isoStore.setCustomSprite(mob.id, url, f, mob.displayName || mob.name);
      }
    }
  }).catch((err) => console.warn('[bestiaryStore] iso seed import failed:', err));
}

// =============================================================================
// STORE
// =============================================================================

interface BestiaryState {
  entries: BestiaryEntry[];
  isLoading: boolean;
  realtimeCleanup: (() => void) | null;

  // Data operations
  loadBestiary: (campaignId: string) => Promise<void>;
  importFromSrd: (campaignId: string, slug: string, tags?: string[]) => Promise<BestiaryEntry>;
  createEntry: (params: Parameters<typeof trpc.bestiary.create.mutate>[0]) => Promise<BestiaryEntry>;
  updateEntry: (id: string, updates: Record<string, unknown>) => Promise<void>;
  removeEntry: (id: string) => Promise<void>;

  // Local queries
  searchLocal: (query: string) => BestiaryEntry[];
  getById: (id: string) => BestiaryEntry | undefined;

  // Module content injection
  injectModuleEntries: (entries: BestiaryEntry[]) => void;
  removeModuleEntries: (moduleNamespace: string) => void;

  // Realtime
  subscribeToRealtime: (campaignId: string) => void;
  unsubscribeFromRealtime: () => void;
}

export const useBestiaryStore = create<BestiaryState>((set, get) => ({
  entries: [],
  isLoading: false,
  realtimeCleanup: null,

  loadBestiary: async (campaignId) => {
    set({ isLoading: true });
    try {
      const entries = await trpc.bestiary.list.query({ campaignId });
      set({ entries, isLoading: false });
    } catch (err) {
      console.error('[bestiaryStore] Failed to load bestiary:', err);
      set({ isLoading: false });
    }
  },

  importFromSrd: async (campaignId, slug, tags) => {
    const entry = await trpc.bestiary.importFromSrd.mutate({ campaignId, slug, tags });
    // Optimistic: add locally (realtime will also fire)
    set((s) => ({ entries: [...s.entries, entry] }));
    return entry;
  },

  createEntry: async (params) => {
    const entry = await trpc.bestiary.create.mutate(params);
    set((s) => ({ entries: [...s.entries, entry] }));
    return entry;
  },

  updateEntry: async (id, updates) => {
    await trpc.bestiary.update.mutate({ id, ...updates });
    // Optimistic local update
    set((s) => ({
      entries: s.entries.map((e) =>
        e.id === id ? { ...e, ...updates, updatedAt: new Date().toISOString() } as BestiaryEntry : e
      ),
    }));
  },

  removeEntry: async (id) => {
    await trpc.bestiary.delete.mutate({ id });
    set((s) => ({ entries: s.entries.filter((e) => e.id !== id) }));
  },

  injectModuleEntries: (entries) => {
    set((s) => {
      // Avoid duplicates by ID
      const existingIds = new Set(s.entries.map((e) => e.id));
      const newEntries = entries.filter((e) => !existingIds.has(e.id));
      return { entries: [...s.entries, ...newEntries] };
    });
  },

  removeModuleEntries: (moduleNamespace) => {
    set((s) => ({
      entries: s.entries.filter((e) => !e.source.startsWith(moduleNamespace)),
    }));
  },

  searchLocal: (query) => {
    const q = query.toLowerCase();
    return get().entries.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.tags.some((t) => t.toLowerCase().includes(q)) ||
        (e.creatureType ?? '').toLowerCase().includes(q)
    );
  },

  getById: (id) => get().entries.find((e) => e.id === id),

  subscribeToRealtime: (campaignId) => {
    get().unsubscribeFromRealtime();

    const { cleanup } = createSubscription({
      channelName: `bestiary:${campaignId}`,
      table: 'machina_bestiary',
      filter: `campaign_id=eq.${campaignId}`,
      event: '*',
      onPayload: (payload) => {
        if (payload.eventType === 'INSERT') {
          const db = payload.new as unknown as DbBestiaryEntry;
          const entry = dbToBestiaryEntry(db);
          set((s) => {
            // Avoid duplicates from optimistic adds
            if (s.entries.some((e) => e.id === entry.id)) return s;
            return { entries: [...s.entries, entry] };
          });
          seedIsoSpritesForBestiaryInstances(entry);
        } else if (payload.eventType === 'UPDATE') {
          const db = payload.new as unknown as DbBestiaryEntry;
          const entry = dbToBestiaryEntry(db);
          set((s) => ({
            entries: s.entries.map((e) => (e.id === entry.id ? entry : e)),
          }));
          // Fire-and-forget seed: if iso_sprites were just saved by the GM,
          // propagate to all matching live mob instances on this client.
          seedIsoSpritesForBestiaryInstances(entry);
        } else if (payload.eventType === 'DELETE') {
          const old = payload.old as { id?: string };
          if (old.id) {
            set((s) => ({ entries: s.entries.filter((e) => e.id !== old.id) }));
          }
        }
      },
    });

    set({ realtimeCleanup: cleanup });
  },

  unsubscribeFromRealtime: () => {
    const { realtimeCleanup } = get();
    if (realtimeCleanup) {
      realtimeCleanup();
      set({ realtimeCleanup: null });
    }
  },
}));
